import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon, Loader2Icon, PlugIcon } from 'lucide-react';
import { useInquiryForm } from '../hooks/useInquiryForm';
import type { InquiryType } from '../types/content';
interface InquiryFormProps {
  initialType: InquiryType | '';
  initialMessage?: string;
}
const inquiryOptions: {
  value: InquiryType;
  label: string;
}[] = [{
  value: 'employer',
  label: 'For Employers — Discuss your hiring needs'
}, {
  value: 'candidate',
  label: 'For Candidates — Introduce yourself'
}, {
  value: 'general',
  label: 'General inquiry'
}, {
  value: 'media',
  label: 'Media & press'
}];
const inputBase = 'mt-2 block w-full rounded-lg border bg-white px-4 py-3 text-[15px] text-midnight-900 placeholder:text-midnight-900/40 transition-[border-color,box-shadow] duration-150 ease-out focus:outline-none focus:ring-2 focus:ring-champagne-400/60 focus:ring-offset-0';
export function InquiryForm({
  initialType,
  initialMessage = ''
}: InquiryFormProps) {
  const {
    values,
    errors,
    status,
    errorMessage,
    update,
    handleSubmit,
    reset
  } = useInquiryForm(initialType, initialMessage);
  const fieldClass = (hasError?: string) => `${inputBase} ${hasError ? 'border-red-700/70' : 'border-midnight-900/15 focus:border-champagne-500'}`;
  if (status === 'success') {
    return <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} className="rounded-lg bg-white p-8 ring-1 ring-midnight-900/10 lg:p-10" role="status" data-id="element-61" data-mp="j4t4efdjw564">
        <CheckCircle2Icon className="h-8 w-8 text-midnight-600" aria-hidden="true" data-id="element-62" data-mp="16vyioq3uepeb" />
        <h3 className="mt-5 font-serif text-3xl text-midnight-900" data-id="element-63" data-mp="11yf6dvqpgmp0">Thank you — your inquiry has been received.</h3>
        <p className="mt-3 text-[15px] leading-relaxed text-midnight-900/70" data-id="element-64" data-mp="38od9keod746v">
          A member of our team will be in touch confidentially, typically within two business days.
        </p>
        <button type="button" onClick={reset} className="mt-8 text-sm font-medium text-midnight-900 underline decoration-midnight-900/30 underline-offset-4 hover:decoration-midnight-900" data-id="element-65" data-mp="17mx92qisahfo">
          Send another inquiry
        </button>
      </motion.div>;
  }
  const submitting = status === 'submitting';
  return <form onSubmit={handleSubmit} noValidate className="rounded-lg bg-white p-6 ring-1 ring-midnight-900/10 sm:p-8 lg:p-10" data-id="element-66" data-mp="1qkpzitc80yw6">
      <div className="grid gap-6 sm:grid-cols-2" data-id="element-67" data-mp="1vij6vpp9fyy1">
        <Field id="name" label="Name" error={errors.name} data-id="element-68" data-mp="1vh849t7jwx8i">
          <input id="inquiry-name" type="text" autoComplete="name" value={values.name} onChange={e => update('name', e.target.value)} className={fieldClass(errors.name)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'inquiry-name-error' : undefined} data-id="element-69" data-mp="20bkmjzxak2io" />
        </Field>
        <Field id="email" label="Email" error={errors.email} data-id="element-70" data-mp="2eb3v2tu0lfvi">
          <input id="inquiry-email" type="email" autoComplete="email" value={values.email} onChange={e => update('email', e.target.value)} className={fieldClass(errors.email)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'inquiry-email-error' : undefined} data-id="element-71" data-mp="2odpou5khkytn" />
        </Field>
        <Field id="organization" label="Organization" optional data-id="element-72" data-mp="2aehld0haj4jn">
          <input id="inquiry-organization" type="text" autoComplete="organization" value={values.organization} onChange={e => update('organization', e.target.value)} className={fieldClass()} data-id="element-73" data-mp="2ag16b4dw63rr" />
        </Field>
        <Field id="inquiryType" label="Inquiry type" error={errors.inquiryType} data-id="element-74" data-mp="327gwt9ikkan9">
          <select id="inquiry-inquiryType" value={values.inquiryType} onChange={e => update('inquiryType', e.target.value as InquiryType | '')} className={fieldClass(errors.inquiryType)} aria-invalid={Boolean(errors.inquiryType)} aria-describedby={errors.inquiryType ? 'inquiry-inquiryType-error' : undefined} data-id="element-75" data-mp="2xcumzas4yq5a">
            <option value="" disabled data-id="element-76" data-mp="3l25fy638vg8v">
              Select an option
            </option>
            {inquiryOptions.map(o => <option key={o.value} value={o.value} data-id="element-77" data-mp="3aluunqoz35x5">
                {o.label}
              </option>)}
          </select>
        </Field>
        <div className="sm:col-span-2" data-id="element-78" data-mp="2wndb8563ofl1">
          <Field id="message" label="Message" error={errors.message} data-id="element-79" data-mp="30mvtet6jeck6">
            <textarea id="inquiry-message" rows={6} value={values.message} onChange={e => update('message', e.target.value)} placeholder={values.inquiryType === 'candidate' ? 'Tell us about your background and the kind of leadership role you are considering.' : 'Tell us about the role, timing, and context of your search.'} className={`${fieldClass(errors.message)} resize-y`} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'inquiry-message-error' : undefined} data-id="element-80" data-mp="30p8cj9o16syt" />
          </Field>
        </div>
      </div>

      <div className="mt-6" data-id="element-81" data-mp="fxg9mi5qjo41">
        <label className="flex items-start gap-3 text-sm leading-relaxed text-midnight-900/75" data-id="element-82" data-mp="fxq1kbacc5cd">
          <input id="inquiry-privacy" type="checkbox" checked={values.privacy} onChange={e => update('privacy', e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded border-midnight-900/30 accent-midnight-800" aria-invalid={Boolean(errors.privacy)} aria-describedby={errors.privacy ? 'inquiry-privacy-error' : undefined} data-id="element-83" data-mp="20m0th8fnme25" />
          <span data-id="element-84" data-mp="25g501ql0ck2p">
            I acknowledge that Qorvessa Executive Partners will use the information I provide solely to respond to this inquiry, and will handle it in strict confidence.
          </span>
        </label>
        {errors.privacy && <ErrorText id="privacy" data-id="element-85" data-mp="1rn6puy3wonf3">{errors.privacy}</ErrorText>}
      </div>

      <AnimatePresence data-id="element-86" data-mp="1rkat3d3so4s8">
        {status === 'not-configured' && <Notice key="nc" tone="info" icon={<PlugIcon className="h-5 w-5" aria-hidden="true" data-id="element-88" data-mp="331smbinr1m0i" />} data-id="element-87" data-mp="1mlzj92di1ua0">
            <p className="font-medium text-midnight-900" data-id="element-89" data-mp="33097lwhqrj0u">Your message has not been sent — online submissions aren’t connected yet.</p>
            <p className="mt-1" data-id="element-90" data-mp="355xmxbhkee94">
              To enable this form, connect a submission service (such as Formspree, Basin, or your own API) and add its URL as
              the inquiry endpoint in the site configuration. The service must accept a JSON POST and return a success (2xx)
              response. Your entries are kept on this page.
            </p>
          </Notice>}
        {status === 'error' && <Notice key="err" tone="error" icon={<AlertCircleIcon className="h-5 w-5" aria-hidden="true" data-id="element-92" data-mp="2mgzhhhgt2tz1" />} data-id="element-91" data-mp="2mfy0kwd3zd9e">
            <p className="font-medium text-midnight-900" data-id="element-93" data-mp="2rdprbn20otc0">Your inquiry was not sent.</p>
            <p className="mt-1" data-id="element-94" data-mp="2wd2fcb4mk6g6">{errorMessage}</p>
          </Notice>}
      </AnimatePresence>

      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-95" data-mp="314u5wlns6c6b">
        <p className="text-xs text-midnight-900/55" data-id="element-96" data-mp="23pgehams7fx0">All fields are required unless marked optional.</p>
        <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-midnight-900 px-7 py-3 text-sm font-medium text-champagne-50 transition-colors duration-150 ease-out hover:bg-midnight-800 disabled:cursor-not-allowed disabled:opacity-70" data-id="element-97" data-mp="28n84fz3v8ryk">
          {submitting && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-98" data-mp="bt49ruuyg3qc" />}
          {submitting ? 'Sending…' : 'Send inquiry'}
        </button>
      </div>
    </form>;
}
function Field({
  id,
  label,
  optional,
  error,
  children
}: {
  id: string;
  label: string;
  optional?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-99" data-mp="3u86gqsky0bp0">
      <label htmlFor={`inquiry-${id}`} className="text-sm font-medium text-midnight-900" data-id="element-100" data-mp="3u7wp70k91wh7">
        {label}
        {optional && <span className="ml-1.5 font-normal text-midnight-900/50" data-id="element-101" data-mp="3p9v5qeiwvv57">(optional)</span>}
      </label>
      {children}
      {error && <ErrorText id={id} data-id="element-102" data-mp="3kas47425471z">{error}</ErrorText>}
    </div>;
}
function ErrorText({
  id,
  children
}: {
  id: string;
  children: React.ReactNode;
}) {
  return <p id={`inquiry-${id}-error`} className="mt-2 text-sm text-red-800" data-id="element-103" data-mp="17zyx37nfdt9k">
      {children}
    </p>;
}
function Notice({
  tone,
  icon,
  children
}: {
  tone: 'info' | 'error';
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return <motion.div role={tone === 'error' ? 'alert' : 'status'} initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className={`mt-6 flex gap-3 rounded-xl p-4 text-sm leading-relaxed text-midnight-900/75 ${tone === 'error' ? 'bg-red-50 ring-1 ring-red-800/20' : 'bg-champagne-50 ring-1 ring-champagne-400/40'}`} data-id="element-104" data-mp="y2u870273jp2">
      <span className={tone === 'error' ? 'text-red-800' : 'text-champagne-600'} data-id="element-105" data-mp="k8a3lnyvayr1">{icon}</span>
      <div data-id="element-106" data-mp="5bixywyloqsz">{children}</div>
    </motion.div>;
}